import { useMemo, useState, useEffect } from 'react'
import useDealStore from '../store/dealStore'
import useOrganizationStore from '../store/organizationStore'
import '../styles/module10.css'

const money = (value) => `$${Math.round(Number(value) || 0).toLocaleString()}`
const numberValue = (value) => Math.round(Number(value) || 0)

// ── Number-to-words helpers ───────────────────────────────────────────────────
const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
]
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

function numToWords(n) {
  n = Math.round(Number(n) || 0)
  if (n === 0) return 'Zero'
  if (n < 0) return 'Negative ' + numToWords(-n)
  let out = ''
  if (n >= 1000000) { out += numToWords(Math.floor(n / 1000000)) + ' Million '; n %= 1000000 }
  if (n >= 1000)    { out += numToWords(Math.floor(n / 1000)) + ' Thousand '; n %= 1000 }
  if (n >= 100)     { out += ONES[Math.floor(n / 100)] + ' Hundred '; n %= 100 }
  if (n >= 20)      { out += TENS[Math.floor(n / 10)] + (n % 10 ? '-' + ONES[n % 10] : '') + ' '; n = 0 }
  else if (n > 0)   { out += ONES[n] + ' ' }
  return out.trim()
}

// ── Derived getters ───────────────────────────────────────────────────────────
function getRecommendedOffer(scoreAndOffer, rawPrice) {
  const offer = scoreAndOffer?.offerPrice
  if (typeof offer === 'number') return offer
  return offer?.recommended || offer?.maximum || offer?.minimum || rawPrice || 0
}

// ── buildInitialTerms ─────────────────────────────────────────────────────────
function buildInitialTerms(dealState) {
  const raw = dealState.rawDealData || {}
  const score = dealState.scoreAndOffer || {}
  const underwriting = dealState.underwritingMetrics || {}
  const actionPlan = dealState.actionPlan || {}
  const dealScore = Number(score.numericalScore) || Number(raw.initialAIScore) || 0
  const purchasePrice = getRecommendedOffer(score, raw.price)
  const risks = score.risks || []
  const urgentTasks = actionPlan.priorities?.filter((t) => t.priority === 'urgent') || []
  const ddDays = risks.length >= 3 ? 30 : 25
  const closingDays = 75
  const capex = numberValue(underwriting.capex?.totalCapEx)
  const today = new Date()
  const dateStr = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

  return {
    // existing terms
    purchasePrice,
    earnestMoneyPct: dealScore >= 80 ? 1.5 : 1,
    dueDiligenceDays: ddDays,
    closingDays,
    financingContingency: true,
    assignmentAllowed: true,
    sellerCredits: capex ? Math.round(capex * 0.25) : Math.round((purchasePrice || 0) * 0.1),
    exclusivityDays: 15,
    expirationDays: 5,
    accessPeriodDays: ddDays,
    closingDepositPct: dealScore >= 80 ? 2 : 1,
    // LOI template fields — parties & brokerage
    date: dateStr,
    sellerName: raw.seller?.name || dealState.sellerName || 'Seller Entity TBD',
    buyerEntityName: dealState.buyerEntityName || 'Buyer Entity LLC',
    buyerManagerEntity: dealState.buyerManagerEntity || 'Buyer Management LLC',
    titleCompany: dealState.titleCompany || 'Vylla Title',
    buyerBroker: dealState.buyerBroker || "Buyer's Broker",
    buyerBrokerage: dealState.buyerBrokerage || 'Listing Leaders Brokerage',
    sellerBrokerage: dealState.sellerBrokerage || 'Marcus & Millichap',
    // LOI property address (editable so user can correct/override what was parsed)
    propertyAddress: raw.location?.address || dealState.dealName || 'Property Address TBD',
    propertyCity:    raw.location?.city    || 'Chicago',
    propertyState:   raw.location?.state   || 'IL',
    propertyZip:     raw.location?.zip     || '',
    propertyName:    dealState.dealName    || 'Active Multifamily Deal',
  }
}

// ── LOI intro sentence (used in AI tools panel) ───────────────────────────────
function buildLoiIntro(dealState, terms) {
  const score = dealState.scoreAndOffer || {}
  const raw = dealState.rawDealData || {}
  const propertyName = terms.propertyName || dealState.dealName || 'the Property'
  const location = [terms.propertyCity, terms.propertyState].filter(Boolean).join(', ')
  const dealScore = Number(score.numericalScore) || Number(raw.initialAIScore) || 0
  const strength =
    dealScore >= 80 ? 'strong underwriting profile' :
    dealScore >= 65 ? 'balanced acquisition profile' :
    'targeted acquisition opportunity'
  return `Buyer is pleased to submit this non-binding Letter of Intent for ${propertyName}${location ? ` in ${location}` : ''}. The proposed terms reflect a ${strength}, a purchase price of ${money(terms.purchasePrice)}, and a transaction structure designed to give Seller clear execution timing while preserving Buyer's required diligence, financing, and approval rights.`
}

// ── buildLoiText — plain-text version (for copy / download) ──────────────────
function buildLoiText(dealState, terms, selectedClauses) {
  const address   = terms.propertyAddress || 'Property Address TBD'
  const city      = terms.propertyCity    || 'Chicago'
  const stateAbbr = terms.propertyState   || 'IL'
  const zip       = terms.propertyZip     || ''

  const purchasePrice    = Number(terms.purchasePrice) || 0
  const sellerCredit     = Number(terms.sellerCredits) || 0
  const netPurchasePrice = purchasePrice - sellerCredit
  const earnestMoney     = Math.round(purchasePrice * (Number(terms.earnestMoneyPct) || 0) / 100)
  const ddWords          = numToWords(terms.dueDiligenceDays)
  const closingWords     = numToWords(terms.closingDays)

  // Operating economics from underwriting (may be absent on early modules)
  const oc = dealState.ownershipCosts || {}
  const hasOpEcon = (oc.totalMonthlyOwnershipCost > 0) || (oc.annualInsurance > 0)
  const opEconLines = hasOpEcon ? [
    "OPERATING ECONOMICS (BUYER'S UNDERWRITING)",
    "The following figures reflect Buyer's internal underwriting and are provided for transparency only.",
    oc.annualInsurance > 0 ? `  Annual Insurance:         ${money(oc.annualInsurance)}${oc.insuranceIsEstimated ? ' (est.)' : ''}` : '',
    oc.annualPropertyTax > 0 ? `  Annual Property Tax:       ${money(oc.annualPropertyTax)}` : '',
    oc.annualPropertyOpEx > 0 ? `  Annual Operating Expenses: ${money(oc.annualPropertyOpEx)}` : '',
    oc.annualDebtService > 0 ? `  Annual Debt Service:       ${money(oc.annualDebtService)}` : '',
    oc.totalMonthlyOwnershipCost > 0 ? `  Total Monthly Cost:        ${money(oc.totalMonthlyOwnershipCost)}/mo` : '',
  ].filter(Boolean).join('\n') : ''
  const opEconSection = opEconLines ? opEconLines + '\n\n' : ''

  const addrLine = zip
    ? `${address}\n${city}, ${stateAbbr} ${zip} (the "Property")`
    : `${address}\n${city}, ${stateAbbr} (the "Property")`

  return `LETTER OF INTENT – COUNTEROFFER

Property:
${addrLine}

Date: ${terms.date}

Seller: ${terms.sellerName}

Buyer: ${terms.buyerEntityName}, or an affiliated entity controlled by ${terms.buyerManagerEntity} ("Buyer").

This non-binding Letter of Intent ("LOI") outlines the principal terms under which Buyer proposes to purchase the Property from Seller and serves as the basis for negotiating a definitive Purchase and Sale Agreement ("PSA").

PURCHASE PRICE
The purchase price for the Property (the "Purchase Price") shall be ${money(purchasePrice)}. At closing, Seller shall provide Buyer a ${money(sellerCredit)} credit toward capital expenditures, resulting in a net purchase price to Buyer of ${money(netPurchasePrice)}.

${opEconSection}PROPERTY APPRAISAL
Buyer will proceed with the purchase if the Property appraises at ${money(netPurchasePrice)} or higher. If the appraisal is below ${money(netPurchasePrice)}, the parties will negotiate in good faith to resolve the appraisal gap.

EFFECTIVE DATE
The "Effective Date" of the PSA shall be the later of the dates upon which the PSA is executed by both Buyer and Seller.

Buyer shall deliver a draft PSA within five (5) business days after this LOI has been accepted by both parties.

INSPECTION PERIOD
Buyer shall have a due diligence period of ${ddWords} (${terms.dueDiligenceDays}) calendar days following the Effective Date of the PSA (the "Inspection Period").

Seller shall convey the Property on an "AS IS, WHERE IS" basis except as may be expressly set forth in the PSA.

SELLER DELIVERABLES
Within three (3) business days after the Effective Date of the PSA, Seller shall provide Buyer with all available documents related to the Property in Seller's possession or control, including but not limited to:
• Current rent roll
• Operating statements for the past three (3) years, if available
• Copies of all leases and amendments
• Service contracts and vendor agreements
• Property tax information
• Surveys, environmental reports, and property condition reports, if available

DEPOSIT
Buyer shall deposit ${money(earnestMoney)} (the "Initial Deposit") with the Title Company escrow within three (3) business days after execution of the PSA.

If Buyer terminates the PSA prior to the expiration of the Inspection Period, the Initial Deposit shall be returned to Buyer.

FINANCING
Buyer's obligation to close may be contingent upon Buyer obtaining financing acceptable to Buyer in its sole discretion. Buyer shall secure a loan commitment within 45 days of executed PSA.

CLOSING
Closing shall occur within ${closingWords} (${terms.closingDays}) days of the executed PSA.

Closing shall occur through ${terms.titleCompany}, which will act as the title company and escrow agent for the transaction.

TITLE
Seller shall convey good and marketable title to the Property and provide a title insurance policy insuring Buyer's title in the amount of the Purchase Price.

ASSIGNMENT
Buyer may assign this LOI and the PSA to an affiliated entity formed for the purpose of completing the transaction.

DUE DILIGENCE ACCESS
Seller shall provide Buyer and Buyer's representatives with reasonable access to the Property during the Inspection Period for purposes of inspections and due diligence.

PREPAYMENT PENALTY
Seller shall be responsible for any penalties or fees associated with the early payoff of any existing liens encumbering the Property.

BROKERAGE
Buyer is represented by ${terms.buyerBroker} of ${terms.buyerBrokerage} as Buyer's agent. Seller agrees that at closing a buyer's brokerage commission equal to one percent (1%) of the final purchase price shall be paid to ${terms.buyerBrokerage}.

${terms.sellerBrokerage} may continue to represent the Seller or act as a cooperating brokerage as applicable.

CONFIDENTIALITY
Buyer agrees that any financial, operational, or proprietary information provided by Seller regarding the Property shall be kept confidential and used solely for evaluating the proposed transaction.

NON-BINDING NATURE
This Letter of Intent is non-binding and intended solely as a framework for negotiating a definitive Purchase and Sale Agreement. Neither party shall have any obligation to proceed with the transaction unless and until a mutually executed PSA is entered into.


BUYER
${terms.buyerEntityName}
By: ${terms.buyerManagerEntity}
Its: Manager
Authorized Signatory

Signature: _________________________
Name: _____________________________
Date: _____________________________


SELLER
${terms.sellerName}
Signature: _________________________
Name: _____________________________
Title: _____________________________`
}

// ── LOI styled document preview (JSX) ─────────────────────────────────────────
function LoiDocumentPreview({ dealState, terms }) {
  const address   = terms.propertyAddress || 'Property Address TBD'
  const city      = terms.propertyCity    || 'Chicago'
  const stateAbbr = terms.propertyState   || 'IL'
  const zip       = terms.propertyZip     || ''

  const purchasePrice    = Number(terms.purchasePrice) || 0
  const sellerCredit     = Number(terms.sellerCredits) || 0
  const netPurchasePrice = purchasePrice - sellerCredit
  const earnestMoney     = Math.round(purchasePrice * (Number(terms.earnestMoneyPct) || 0) / 100)
  const ddWords          = numToWords(terms.dueDiligenceDays)
  const closingWords     = numToWords(terms.closingDays)

  const doc = {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: '12px',
    lineHeight: '1.65',
    color: '#000',
    background: '#fff',
    padding: '56px 64px',
    maxWidth: '760px',
    margin: '0 auto',
    boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
  }
  const center  = { textAlign: 'center' }
  const bold    = { fontWeight: 'bold' }
  const heading = { fontWeight: 'bold', marginTop: '18px', marginBottom: '4px' }
  const para    = { marginBottom: '12px' }
  const ul      = { paddingLeft: '20px', marginBottom: '12px', listStyleType: 'disc' }
  const sigBlock = { marginTop: '36px' }
  const sigLine  = {
    borderBottom: '1px solid #000',
    display: 'inline-block',
    minWidth: '220px',
    marginLeft: '8px',
    verticalAlign: 'bottom',
  }

  return (
    <div style={doc}>
      {/* ── Header ── */}
      <div style={{ ...center, ...bold, fontSize: '13px', letterSpacing: '0.5px', marginBottom: '24px' }}>
        LETTER OF INTENT – COUNTEROFFER
      </div>
      <div style={{ ...center, marginBottom: '28px' }}>
        <div>Property:</div>
        <div style={bold}>{address}</div>
        <div style={bold}>{city}, {stateAbbr}{zip ? ' ' + zip : ''} (the "Property")</div>
      </div>

      {/* ── Date / Parties ── */}
      <p style={para}>Date: {terms.date}</p>
      <p style={para}><span style={bold}>Seller:</span> {terms.sellerName}</p>
      <p style={para}><span style={bold}>Buyer:</span> {terms.buyerEntityName}, or an affiliated entity controlled by {terms.buyerManagerEntity} (&ldquo;Buyer&rdquo;).</p>
      <p style={para}>
        This non-binding Letter of Intent (&ldquo;LOI&rdquo;) outlines the principal terms under which Buyer
        proposes to purchase the Property from Seller and serves as the basis for negotiating a definitive
        Purchase and Sale Agreement (&ldquo;PSA&rdquo;).
      </p>

      {/* ── PURCHASE PRICE ── */}
      <p style={heading}>PURCHASE PRICE</p>
      <p style={para}>
        The purchase price for the Property (the &ldquo;Purchase Price&rdquo;) shall be {money(purchasePrice)}. At closing, Seller
        shall provide Buyer a {money(sellerCredit)} credit toward capital expenditures, resulting in a net purchase
        price to Buyer of {money(netPurchasePrice)}.
      </p>

      {/* ── PROPERTY APPRAISAL ── */}
      <p style={heading}>PROPERTY APPRAISAL</p>
      <p style={para}>
        Buyer will proceed with the purchase if the Property appraises at {money(netPurchasePrice)} or higher. If the
        appraisal is below {money(netPurchasePrice)}, the parties will negotiate in good faith to resolve the appraisal gap.
      </p>

      {/* ── EFFECTIVE DATE ── */}
      <p style={heading}>EFFECTIVE DATE</p>
      <p style={para}>
        The &ldquo;Effective Date&rdquo; of the PSA shall be the later of the dates upon which the PSA is executed
        by both Buyer and Seller.
      </p>
      <p style={para}>
        Buyer shall deliver a draft PSA within five (5) business days after this LOI has been accepted by
        both parties.
      </p>

      {/* ── INSPECTION PERIOD ── */}
      <p style={heading}>INSPECTION PERIOD</p>
      <p style={para}>
        Buyer shall have a due diligence period of {ddWords} ({terms.dueDiligenceDays}) calendar days following the Effective
        Date of the PSA (the &ldquo;Inspection Period&rdquo;).
      </p>
      <p style={para}>
        Seller shall convey the Property on an &ldquo;AS IS, WHERE IS&rdquo; basis except as may be expressly set
        forth in the PSA.
      </p>

      {/* ── SELLER DELIVERABLES ── */}
      <p style={heading}>SELLER DELIVERABLES</p>
      <p style={para}>
        Within three (3) business days after the Effective Date of the PSA, Seller shall provide Buyer
        with all available documents related to the Property in Seller&rsquo;s possession or control, including
        but not limited to:
      </p>
      <ul style={ul}>
        <li>Current rent roll</li>
        <li>Operating statements for the past three (3) years, if available</li>
        <li>Copies of all leases and amendments</li>
        <li>Service contracts and vendor agreements</li>
        <li>Property tax information</li>
        <li>Surveys, environmental reports, and property condition reports, if available</li>
      </ul>

      {/* ── DEPOSIT ── */}
      <p style={heading}>DEPOSIT</p>
      <p style={para}>
        Buyer shall deposit {money(earnestMoney)} (the &ldquo;Initial Deposit&rdquo;) with the Title Company escrow within three
        (3) business days after execution of the PSA.
      </p>
      <p style={para}>
        If Buyer terminates the PSA prior to the expiration of the Inspection Period, the Initial Deposit
        shall be returned to Buyer.
      </p>

      {/* ── FINANCING ── */}
      <p style={heading}>FINANCING</p>
      <p style={para}>
        Buyer&rsquo;s obligation to close may be contingent upon Buyer obtaining financing acceptable to
        Buyer in its sole discretion. Buyer shall secure a loan commitment within 45 days of executed PSA.
      </p>

      {/* ── CLOSING ── */}
      <p style={heading}>CLOSING</p>
      <p style={para}>
        Closing shall occur within {closingWords} ({terms.closingDays}) days of the executed PSA.
      </p>
      <p style={para}>
        Closing shall occur through {terms.titleCompany}, which will act as the title company and escrow agent for
        the transaction.
      </p>

      {/* ── TITLE ── */}
      <p style={heading}>TITLE</p>
      <p style={para}>
        Seller shall convey good and marketable title to the Property and provide a title insurance policy
        insuring Buyer&rsquo;s title in the amount of the Purchase Price.
      </p>

      {/* ── ASSIGNMENT ── */}
      <p style={heading}>ASSIGNMENT</p>
      <p style={para}>
        Buyer may assign this LOI and the PSA to an affiliated entity formed for the purpose of
        completing the transaction.
      </p>

      {/* ── DUE DILIGENCE ACCESS ── */}
      <p style={heading}>DUE DILIGENCE ACCESS</p>
      <p style={para}>
        Seller shall provide Buyer and Buyer&rsquo;s representatives with reasonable access to the Property
        during the Inspection Period for purposes of inspections and due diligence.
      </p>

      {/* ── PREPAYMENT PENALTY ── */}
      <p style={heading}>PREPAYMENT PENALTY</p>
      <p style={para}>
        Seller shall be responsible for any penalties or fees associated with the early payoff of any
        existing liens encumbering the Property.
      </p>

      {/* ── BROKERAGE ── */}
      <p style={heading}>BROKERAGE</p>
      <p style={para}>
        Buyer is represented by {terms.buyerBroker} of {terms.buyerBrokerage} as Buyer&rsquo;s agent. Seller
        agrees that at closing a buyer&rsquo;s brokerage commission equal to one percent (1%) of the final
        purchase price shall be paid to {terms.buyerBrokerage}.
      </p>
      <p style={para}>
        {terms.sellerBrokerage} may continue to represent the Seller or act as a cooperating brokerage as
        applicable.
      </p>

      {/* ── CONFIDENTIALITY ── */}
      <p style={heading}>CONFIDENTIALITY</p>
      <p style={para}>
        Buyer agrees that any financial, operational, or proprietary information provided by Seller
        regarding the Property shall be kept confidential and used solely for evaluating the proposed
        transaction.
      </p>

      {/* ── NON-BINDING NATURE ── */}
      <p style={heading}>NON-BINDING NATURE</p>
      <p style={{ ...para, marginBottom: '32px' }}>
        This Letter of Intent is non-binding and intended solely as a framework for negotiating a
        definitive Purchase and Sale Agreement. Neither party shall have any obligation to proceed with
        the transaction unless and until a mutually executed PSA is entered into.
      </p>

      {/* ── BUYER SIG ── */}
      <div style={sigBlock}>
        <p style={bold}>BUYER</p>
        <p style={{ margin: '0 0 2px' }}>{terms.buyerEntityName}</p>
        <p style={{ margin: '0 0 2px' }}>By: {terms.buyerManagerEntity}</p>
        <p style={{ margin: '0 0 2px' }}>Its: Manager</p>
        <p style={{ margin: '0 0 18px' }}>Authorized Signatory</p>
        <p style={{ margin: '0 0 10px' }}>Signature: <span style={sigLine}>&nbsp;</span></p>
        <p style={{ margin: '0 0 10px' }}>Name: <span style={sigLine}>&nbsp;</span></p>
        <p style={{ margin: '0 0 10px' }}>Date: <span style={sigLine}>&nbsp;</span></p>
      </div>

      {/* ── SELLER SIG ── */}
      <div style={{ ...sigBlock, marginTop: '48px' }}>
        <p style={bold}>SELLER</p>
        <p style={{ margin: '0 0 18px' }}>{terms.sellerName}</p>
        <p style={{ margin: '0 0 10px' }}>Signature: <span style={sigLine}>&nbsp;</span></p>
        <p style={{ margin: '0 0 10px' }}>Name: <span style={sigLine}>&nbsp;</span></p>
        <p style={{ margin: '0 0 10px' }}>Title: <span style={sigLine}>&nbsp;</span></p>
      </div>
    </div>
  )
}

// ── Contingency clauses ───────────────────────────────────────────────────────
const CLAUSES = [
  'Buyer approval of rent roll, T12 financials, leases, service contracts, and operating statements.',
  'Buyer approval of physical inspections, property condition, environmental review, and third-party reports.',
  'Buyer approval of title, survey, zoning, permits, and any recorded exceptions.',
  'Buyer approval of final debt terms, lender conditions, and capital stack requirements.',
  'Seller to provide reasonable access to property, records, vendors, and management team during diligence.',
]

const RISK_CLAUSES = [
  'Buyer approval of insurance quotes, tax reassessment exposure, and updated operating expense assumptions.',
  'Buyer approval of capital expenditure scope, vendor bids, and any deferred maintenance discovered during diligence.',
  'Buyer approval of market rent assumptions, occupancy trends, and concessions before expiration of diligence.',
]

function buildConsistencyChecks(dealState, terms, selectedClauses) {
  const offerPrice = getRecommendedOffer(dealState.scoreAndOffer || {}, dealState.rawDealData?.price)
  return [
    { label: 'Offer price matches underwriting recommendation', pass: Math.abs(Number(terms.purchasePrice) - Number(offerPrice || 0)) <= Math.max(5000, Number(offerPrice || 0) * 0.03) },
    { label: 'Due diligence window is long enough for selected contingencies', pass: Number(terms.dueDiligenceDays) >= (selectedClauses.length >= 4 ? 45 : 30) },
    { label: 'Closing timeline follows diligence period', pass: Number(terms.closingDays) >= Number(terms.dueDiligenceDays) },
    { label: 'Earnest money is within market range', pass: Number(terms.earnestMoneyPct) >= 0.5 && Number(terms.earnestMoneyPct) <= 3 },
  ]
}

function buildRiskClauses(dealState) {
  const risks = dealState.scoreAndOffer?.risks || []
  if (!risks.length) return RISK_CLAUSES.slice(0, 1)
  return RISK_CLAUSES.slice(0, Math.min(3, risks.length))
}

// ── Main component ────────────────────────────────────────────────────────────
function Module10Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore()
  const { profile: orgProfile } = useOrganizationStore()

  // Merge org profile into initial LOI terms so buyer name/entity auto-fills
  const initialTerms = useMemo(() => {
    const base = buildInitialTerms(dealState)
    if (orgProfile?.companyName) {
      base.buyerEntityName = dealState.buyerEntityName || `${orgProfile.companyName}, LLC`
      base.buyerManagerEntity = dealState.buyerManagerEntity || orgProfile.companyName
    }
    return base
  }, [dealState, orgProfile])
  const [terms, setTerms] = useState(() => ({
    ...initialTerms,
    ...(dealState.loiDraft?.terms || {}),
  }))
  const [selectedClauses, setSelectedClauses] = useState(
    dealState.loiDraft?.selectedClauses ?? CLAUSES
  )
  const [activeTab, setActiveTab] = useState('terms')

  // Auto-persist LOI draft state on every change
  useEffect(() => {
    updateDealState({ loiDraft: { terms, selectedClauses } })
  }, [terms, selectedClauses]) // eslint-disable-line
  const [copied, setCopied] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editedLoiText, setEditedLoiText] = useState(null)

  const raw = dealState.rawDealData || {}
  const actionPlan = dealState.actionPlan || {}
  const ownershipCosts = dealState.ownershipCosts || {}
  // Use terms for property info — terms is the editable source of truth
  const propertyName = terms.propertyName || dealState.dealName || 'Active Multifamily Deal'
  const location = [terms.propertyCity, terms.propertyState].filter(Boolean).join(', ') || 'Target market'
  const loiText = useMemo(() => buildLoiText(dealState, terms, selectedClauses), [dealState, terms, selectedClauses])
  const earnestMoney = Math.round((Number(terms.purchasePrice) || 0) * (Number(terms.earnestMoneyPct) || 0) / 100)
  const highPriorityTasks = (actionPlan.priorities || actionPlan.tasks || []).filter((t) => ['urgent', 'high'].includes(t.priority))
  const completedActions = actionPlan.statusTracking?.completedTasks || 0
  const readinessScore = Math.min(100, 70 + selectedClauses.length * 4 + Math.min(highPriorityTasks.length, 4) * 2 + completedActions * 2)
  const closingDeposit = Math.round((Number(terms.purchasePrice) || 0) * (Number(terms.closingDepositPct) || 0) / 100)
  const loiIntro = useMemo(() => buildLoiIntro(dealState, terms), [dealState, terms])
  const consistencyChecks = useMemo(() => buildConsistencyChecks(dealState, terms, selectedClauses), [dealState, terms, selectedClauses])
  const consistencyScore = consistencyChecks.filter((c) => c.pass).length
  const riskClauses = useMemo(() => buildRiskClauses(dealState), [dealState])
  const missingRiskClauses = riskClauses.filter((c) => !selectedClauses.includes(c))
  const allClauses = useMemo(() => [...new Set([...CLAUSES, ...riskClauses])], [riskClauses])

  const tabs = [
    { id: 'terms', icon: '1', label: 'Offer Terms' },
    { id: 'contingencies', icon: '2', label: 'Contingencies' },
    { id: 'preview', icon: '3', label: 'LOI Preview' },
  ]

  const updateTerm = (key, value) => {
    const STRING_TERM_KEYS = new Set([
      'date', 'sellerName', 'buyerEntityName', 'buyerManagerEntity',
      'titleCompany', 'buyerBroker', 'buyerBrokerage', 'sellerBrokerage',
      'propertyName', 'propertyAddress', 'propertyCity', 'propertyState', 'propertyZip',
    ])
    setTerms((cur) => ({
      ...cur,
      [key]: ['financingContingency', 'assignmentAllowed'].includes(key)
        ? value
        : STRING_TERM_KEYS.has(key)
          ? value
          : Number(value),
    }))
  }

  const toggleClause = (clause) => {
    setSelectedClauses((cur) =>
      cur.includes(clause) ? cur.filter((c) => c !== clause) : [...cur, clause]
    )
  }


  const applyRiskClauses = () => {
    setSelectedClauses((cur) => [...new Set([...cur, ...riskClauses])])
    setActiveTab('contingencies')
  }

  const loiOutput = editedLoiText !== null ? editedLoiText : loiText

  const handleCopy = () => {
    navigator.clipboard.writeText(loiOutput).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  const handleDownload = () => {
    // Task 8: Generate LOI as .docx (Word-compatible HTML blob)
    const dealName = terms.propertyName || propertyName || 'Property'
    const safeName = dealName.replace(/[^a-z0-9]+/gi, '_') || 'LOI'
    const dateStr  = new Date().toISOString().split('T')[0].replace(/-/g, '')
    const filename = `LOI_${safeName}_${dateStr}.docx`

    const pprice = Number(terms.purchasePrice) || 0
    const credit = Number(terms.sellerCredits) || 0
    const netPP  = pprice - credit
    const earnest = Math.round(pprice * (Number(terms.earnestMoneyPct) || 0) / 100)

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"/><title>Letter of Intent</title>
      <style>
        body    { font-family:"Times New Roman",Times,serif; font-size:12pt; color:#000; margin:0; }
        .page   { padding:56pt 64pt; max-width:780pt; margin:0 auto; }
        h1      { text-align:center; font-weight:bold; font-size:13pt; letter-spacing:0.5pt; margin-bottom:20pt; }
        h2      { font-weight:bold; font-size:12pt; margin-top:16pt; margin-bottom:4pt; }
        p       { text-align:justify; line-height:1.65; margin-bottom:10pt; }
        .center { text-align:center; }
        .sigblk { margin-top:36pt; }
        .sigline{ border-bottom:1pt solid #000; display:inline-block; min-width:220pt; }
      </style></head>
      <body><div class="page">
        <h1>LETTER OF INTENT – COUNTEROFFER</h1>
        <p class="center"><strong>${terms.propertyAddress || ''}</strong><br/>
        ${terms.propertyCity || ''}, ${terms.propertyState || ''}${terms.propertyZip ? ' ' + terms.propertyZip : ''} (the "Property")</p>
        <p>Date: ${terms.date || new Date().toLocaleDateString()}</p>
        <p><strong>Seller:</strong> ${terms.sellerName || ''}</p>
        <p><strong>Buyer:</strong> ${terms.buyerEntityName || ''}, or an affiliated entity controlled by ${terms.buyerManagerEntity || ''} ("Buyer").</p>
        <p>This non-binding Letter of Intent ("LOI") outlines the principal terms under which Buyer proposes to purchase the Property from Seller.</p>
        <h2>PURCHASE PRICE</h2>
        <p>The Purchase Price shall be $${pprice.toLocaleString()}. At closing, Seller shall provide Buyer a $${credit.toLocaleString()} credit toward capital expenditures, resulting in a net purchase price of $${netPP.toLocaleString()}.</p>
        <h2>DEPOSIT</h2>
        <p>Buyer shall deposit $${earnest.toLocaleString()} (the "Initial Deposit") with the Title Company escrow within three (3) business days after execution of the PSA.</p>
        <h2>INSPECTION PERIOD</h2>
        <p>Buyer shall have a due diligence period of ${terms.dueDiligenceDays || 25} calendar days following the Effective Date of the PSA.</p>
        <h2>CLOSING</h2>
        <p>Closing shall occur on or before ${terms.closingDays || 45} days after the expiration of the Inspection Period.</p>
        <h2>FINANCING CONTINGENCY</h2>
        <p>This LOI is subject to Buyer obtaining satisfactory financing at terms acceptable to Buyer in its sole discretion.</p>
        <h2>NON-BINDING NATURE</h2>
        <p>This Letter of Intent is non-binding and intended solely as a framework for negotiating a definitive Purchase and Sale Agreement.</p>
        <div class="sigblk">
          <p><strong>BUYER</strong><br/>${terms.buyerEntityName || ''}<br/>By: ${terms.buyerManagerEntity || ''}<br/>
          Signature: <span class="sigline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span><br/>Date: <span class="sigline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span></p>
          <p><strong>SELLER</strong><br/>${terms.sellerName || ''}<br/>
          Signature: <span class="sigline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span><br/>Date: <span class="sigline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span></p>
        </div>
      </div></body></html>`

    const blob = new Blob([html], { type: 'application/msword;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    setDownloaded(true)
  }

  const handleComplete = () => {
    const generatedAt = new Date().toISOString()
    const documentId = `loi_${generatedAt.replace(/\D/g, '')}`
    updateDealState({
      loiDocument: {
        documentId,
        offerPrice: Number(terms.purchasePrice),
        timeline: {
          dueDiligenceDays: Number(terms.dueDiligenceDays),
          closingDays: Number(terms.closingDays),
          exclusivityDays: Number(terms.exclusivityDays),
          expirationDays: Number(terms.expirationDays),
          accessPeriodDays: Number(terms.accessPeriodDays),
        },
        earnestMoney: {
          amount: earnestMoney,
          percentage: Number(terms.earnestMoneyPct),
        },
        contingencies: selectedClauses,
        generatedAt,
        financingContingency: terms.financingContingency,
        assignmentAllowed: terms.assignmentAllowed,
        sellerCredits: Number(terms.sellerCredits),
        closingDeposit: {
          amount: closingDeposit,
          percentage: Number(terms.closingDepositPct),
        },
        documentText: loiOutput,
        sourceActionPlan: {
          priorityTasks: highPriorityTasks.map((t) => t.id || t.title),
          momentumScore: actionPlan.statusTracking?.momentumScore || null,
        },
        consistencyValidation: consistencyChecks,
        riskAwareClauses: riskClauses,
        aiIntro: loiIntro,
        // NEW — party / brokerage fields
        sellerName: terms.sellerName,
        buyerEntityName: terms.buyerEntityName,
        buyerManagerEntity: terms.buyerManagerEntity,
        titleCompany: terms.titleCompany,
        buyerBroker: terms.buyerBroker,
        buyerBrokerage: terms.buyerBrokerage,
        sellerBrokerage: terms.sellerBrokerage,
        date: terms.date,
      },
    })
    updateAvailableModules()
    setTimeout(() => forceNavigateToModule(12), 500)
  }

  return (
    <div className="m10-page">
      <div className="m10-header">
        <div className="m10-badge">Module 10</div>
        <div className="m10-header-text">
          <h1>LOI Generator</h1>
          <p>Create a professional Letter of Intent using deal score, offer terms, and Module 8 execution priorities.</p>
        </div>
        <div className="m10-header-stats">
          <div>
            <span>LOI Readiness</span>
            <strong>{readinessScore}%</strong>
          </div>
          <div>
            <span>Action Inputs</span>
            <strong>{highPriorityTasks.length}</strong>
          </div>
        </div>
      </div>

      <div className="m10-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="m10-tab-content">
        {/* ════════════════════ TAB 1 — TERMS ════════════════════ */}
        {activeTab === 'terms' && (
        <>
        <section className="m10-summary-card">
          <div className="m10-title-row">
            <span>Deal Summary</span>
            <strong>Auto-Filled</strong>
          </div>
          <h2>{propertyName}</h2>
          <p>{location}</p>
          <div className="m10-summary-grid">
            <div><span>Offer Price</span><strong>{money(terms.purchasePrice)}</strong></div>
            <div><span>Earnest Money</span><strong>{money(earnestMoney)}</strong></div>
            <div><span>Due Diligence</span><strong>{terms.dueDiligenceDays} days</strong></div>
            <div><span>Closing</span><strong>{terms.closingDays} days</strong></div>
          </div>
          {(ownershipCosts.totalMonthlyOwnershipCost || ownershipCosts.annualInsurance) && (
            <div className="m10-ownership-economics">
              <div className="m10-oe-title">Operating Economics (from Underwriting)</div>
              <div className="m10-oe-grid">
                {ownershipCosts.monthlyDebtService > 0 && (
                  <div><span>Monthly Debt Service</span><strong>{money(ownershipCosts.monthlyDebtService)}</strong></div>
                )}
                {ownershipCosts.annualInsurance > 0 && (
                  <div>
                    <span>Annual Insurance{ownershipCosts.insuranceIsEstimated ? <em className="m10-est"> est.</em> : ''}</span>
                    <strong>{money(ownershipCosts.annualInsurance)}</strong>
                  </div>
                )}
                {ownershipCosts.annualPropertyTax > 0 && (
                  <div><span>Annual Property Tax</span><strong>{money(ownershipCosts.annualPropertyTax)}</strong></div>
                )}
                {ownershipCosts.annualPropertyOpEx > 0 && (
                  <div><span>Annual Op. Expenses</span><strong>{money(ownershipCosts.annualPropertyOpEx)}</strong></div>
                )}
                {ownershipCosts.totalMonthlyOwnershipCost > 0 && (
                  <div className="m10-oe-total"><span>Total Monthly Cost</span><strong>{money(ownershipCosts.totalMonthlyOwnershipCost)}</strong></div>
                )}
                {ownershipCosts.totalAnnualOwnershipCost > 0 && (
                  <div className="m10-oe-total"><span>Total Annual Cost</span><strong>{money(ownershipCosts.totalAnnualOwnershipCost)}</strong></div>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="m10-terms-card">
          <div className="m10-title-row">
            <span>Offer Terms</span>
            <strong>Editable</strong>
          </div>
          <div className="m10-form-grid">
            <label>
              <span>Purchase Price</span>
              <input type="number" value={terms.purchasePrice} onChange={(e) => updateTerm('purchasePrice', e.target.value)} />
            </label>
            <label>
              <span>Earnest Money %</span>
              <input type="number" step="0.25" value={terms.earnestMoneyPct} onChange={(e) => updateTerm('earnestMoneyPct', e.target.value)} />
            </label>
            <label>
              <span>Due Diligence Days</span>
              <input type="number" value={terms.dueDiligenceDays} onChange={(e) => updateTerm('dueDiligenceDays', e.target.value)} />
            </label>
            <label>
              <span>Closing Days</span>
              <input type="number" value={terms.closingDays} onChange={(e) => updateTerm('closingDays', e.target.value)} />
            </label>
            <label>
              <span>Seller Credit / CapEx</span>
              <input type="number" value={terms.sellerCredits} onChange={(e) => updateTerm('sellerCredits', e.target.value)} />
            </label>
            <label>
              <span>Exclusivity Days</span>
              <input type="number" value={terms.exclusivityDays} onChange={(e) => updateTerm('exclusivityDays', e.target.value)} />
            </label>
            <label>
              <span>Offer Expiration Days</span>
              <input type="number" value={terms.expirationDays} onChange={(e) => updateTerm('expirationDays', e.target.value)} />
            </label>
            <label>
              <span>Property Access Days</span>
              <input type="number" value={terms.accessPeriodDays} onChange={(e) => updateTerm('accessPeriodDays', e.target.value)} />
            </label>
            <label>
              <span>Closing Deposit %</span>
              <input type="number" step="0.25" value={terms.closingDepositPct} onChange={(e) => updateTerm('closingDepositPct', e.target.value)} />
            </label>
            {/* ── NEW LOI template fields ── */}
            <label>
              <span>LOI Date</span>
              <input type="text" value={terms.date} onChange={(e) => updateTerm('date', e.target.value)} />
            </label>
            <label>
              <span>Property Name</span>
              <input type="text" value={terms.propertyName} onChange={(e) => updateTerm('propertyName', e.target.value)} />
            </label>
            <label>
              <span>Property Address</span>
              <input type="text" value={terms.propertyAddress} onChange={(e) => updateTerm('propertyAddress', e.target.value)} />
            </label>
            <label>
              <span>City</span>
              <input type="text" value={terms.propertyCity} onChange={(e) => updateTerm('propertyCity', e.target.value)} />
            </label>
            <label>
              <span>State</span>
              <input type="text" value={terms.propertyState} onChange={(e) => updateTerm('propertyState', e.target.value)} />
            </label>
            <label>
              <span>Zip</span>
              <input type="text" value={terms.propertyZip} onChange={(e) => updateTerm('propertyZip', e.target.value)} />
            </label>
            <label>
              <span>Seller Name / Entity</span>
              <input type="text" value={terms.sellerName} onChange={(e) => updateTerm('sellerName', e.target.value)} />
            </label>
            <label>
              <span>Buyer Entity Name</span>
              <input type="text" value={terms.buyerEntityName} onChange={(e) => updateTerm('buyerEntityName', e.target.value)} />
            </label>
            <label>
              <span>Buyer Manager / Controlled By</span>
              <input type="text" value={terms.buyerManagerEntity} onChange={(e) => updateTerm('buyerManagerEntity', e.target.value)} />
            </label>
            <label>
              <span>Title Company</span>
              <input type="text" value={terms.titleCompany} onChange={(e) => updateTerm('titleCompany', e.target.value)} />
            </label>
            <label>
              <span>Buyer&apos;s Broker Name</span>
              <input type="text" value={terms.buyerBroker} onChange={(e) => updateTerm('buyerBroker', e.target.value)} />
            </label>
            <label>
              <span>Buyer&apos;s Brokerage</span>
              <input type="text" value={terms.buyerBrokerage} onChange={(e) => updateTerm('buyerBrokerage', e.target.value)} />
            </label>
            <label>
              <span>Seller&apos;s Brokerage</span>
              <input type="text" value={terms.sellerBrokerage} onChange={(e) => updateTerm('sellerBrokerage', e.target.value)} />
            </label>
          </div>
          <div className="m10-toggle-row">
            <label>
              <input type="checkbox" checked={terms.financingContingency} onChange={(e) => updateTerm('financingContingency', e.target.checked)} />
              <span>Financing contingency</span>
            </label>
            <label>
              <input type="checkbox" checked={terms.assignmentAllowed} onChange={(e) => updateTerm('assignmentAllowed', e.target.checked)} />
              <span>Assignment allowed</span>
            </label>
          </div>
          <div className="m10-term-insights">
            <div>
              <span>Economics</span>
              <strong>{money(earnestMoney + closingDeposit)}</strong>
              <p>Total initial deposit exposure before closing.</p>
            </div>
            <div>
              <span>Seller Credit</span>
              <strong>{money(terms.sellerCredits)}</strong>
              <p>CapEx credit applied at closing; reduces net purchase price.</p>
            </div>
            <div>
              <span>Timeline</span>
              <strong>{Number(terms.dueDiligenceDays) + Number(terms.closingDays)} days</strong>
              <p>Estimated path from LOI acceptance to closing.</p>
            </div>
            <div>
              <span>AI Position</span>
              <strong>{readinessScore >= 90 ? 'Strong' : 'Review'}</strong>
              <p>{readinessScore >= 90 ? 'Terms are balanced for seller confidence and buyer protection.' : 'Review contingencies before sending.'}</p>
            </div>
          </div>
        </section>

        <section className="m10-action-card">
          <div className="m10-title-row">
            <span>Module 9 Handoff</span>
            <strong>Connected</strong>
          </div>
          <div className="m10-action-list">
            {highPriorityTasks.slice(0, 5).map((task) => (
              <div key={task.id || task.title} className={`m10-action-row ${task.priority}`}>
                <span>{task.priority}</span>
                <strong>{task.title}</strong>
              </div>
            ))}
            {!highPriorityTasks.length && (
              <p className="m10-empty-note">No urgent actions were found. Module 10 is using standard LOI terms.</p>
            )}
          </div>
          <div className="m10-momentum-box">
            <span>Deal Momentum</span>
            <strong>{actionPlan.statusTracking?.momentumScore || 'Pending'}</strong>
          </div>
        </section>

        <section className="m10-ai-card">
          <div className="m10-title-row">
            <span>LOI Intelligence Tools</span>
            <strong>{consistencyScore}/{consistencyChecks.length} valid</strong>
          </div>
          <div className="m10-ai-grid">
            <div className="m10-ai-tool">
              <strong>Context-Aware Drafting</strong>
              <p>{loiIntro}</p>
              <button onClick={() => setActiveTab('preview')}>Review Draft</button>
            </div>
            <div className="m10-ai-tool">
              <strong>Consistency Validation</strong>
              <div className="m10-validation-list">
                {consistencyChecks.map((check) => (
                  <div key={check.label} className={check.pass ? 'pass' : 'warn'}>
                    <span>{check.pass ? '✓' : '!'}</span>
                    {check.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="m10-ai-tool">
              <strong>Risk-Aware Recommendations</strong>
              <p>{missingRiskClauses.length ? `${missingRiskClauses.length} protective clause${missingRiskClauses.length === 1 ? '' : 's'} can be added from deal risks.` : 'Protective clauses are already included.'}</p>
              <button onClick={applyRiskClauses}>Apply Risk Clauses</button>
            </div>
          </div>
        </section>
        </>
        )}

        {/* ════════════════════ TAB 3 — PREVIEW ════════════════════ */}
        {activeTab === 'preview' && (
        <>
        <section className="m10-preview-card">
          <div className="m10-title-row">
            <span>LOI Preview</span>
            <div className="m10-preview-mode-toggle">
              <button
                className={`m10-mode-btn ${!editMode ? 'active' : ''}`}
                onClick={() => setEditMode(false)}
              >📄 Formatted</button>
              <button
                className={`m10-mode-btn ${editMode ? 'active' : ''}`}
                onClick={() => { setEditMode(true); if (editedLoiText === null) setEditedLoiText(loiText) }}
              >✏️ Edit Text</button>
              {editedLoiText !== null && (
                <button
                  className="m10-mode-btn m10-mode-btn--reset"
                  onClick={() => { setEditedLoiText(null); setEditMode(false) }}
                  title="Reset to auto-generated text"
                >↺ Reset</button>
              )}
            </div>
          </div>

          {editMode ? (
            <div className="m10-loi-edit-wrap">
              <p className="m10-edit-hint">Edit the LOI text directly below. Changes are saved automatically and used for copy/download.</p>
              <textarea
                className="m10-loi-textarea"
                value={editedLoiText !== null ? editedLoiText : loiText}
                onChange={(e) => setEditedLoiText(e.target.value)}
                spellCheck={true}
              />
            </div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: '640px', border: '1px solid #d0d5dd', borderRadius: '6px', marginBottom: '12px' }}>
              <LoiDocumentPreview dealState={dealState} terms={terms} />
            </div>
          )}

          <div className="m10-preview-actions">
            <button onClick={handleCopy}>{copied ? 'Copied' : 'Copy LOI Text'}</button>
            <button onClick={handleDownload}>{downloaded ? '✓ Downloaded' : 'Download LOI (.docx)'}</button>
            {editedLoiText !== null && <span className="m10-edited-badge">✎ Edited</span>}
          </div>
        </section>

        <section className="m10-summary-card">
          <div className="m10-title-row">
            <span>Preview Summary</span>
            <strong>Ready to Send</strong>
          </div>
          <h2>{propertyName}</h2>
          <p>{location}</p>
          <div className="m10-summary-grid">
            <div><span>Purchase Price</span><strong>{money(terms.purchasePrice)}</strong></div>
            <div><span>Earnest Money</span><strong>{money(earnestMoney)}</strong></div>
            <div><span>Contingencies</span><strong>{selectedClauses.length}</strong></div>
            <div><span>Readiness</span><strong>{readinessScore}%</strong></div>
            {ownershipCosts.totalMonthlyOwnershipCost > 0 && (
              <div><span>Total Monthly Cost</span><strong>{money(ownershipCosts.totalMonthlyOwnershipCost)}</strong></div>
            )}
            {ownershipCosts.annualInsurance > 0 && (
              <div>
                <span>Annual Insurance{ownershipCosts.insuranceIsEstimated ? <em className="m10-est"> est.</em> : ''}</span>
                <strong>{money(ownershipCosts.annualInsurance)}</strong>
              </div>
            )}
          </div>
        </section>
        </>
        )}

        {/* ════════════════════ TAB 2 — CONTINGENCIES ════════════════════ */}
        {activeTab === 'contingencies' && (
        <>
        <section className="m10-clause-card">
          <div className="m10-title-row">
            <span>Contingencies</span>
            <strong>{selectedClauses.length} Selected</strong>
          </div>
          <div className="m10-clause-list">
            {allClauses.map((clause) => (
              <label key={clause} className={selectedClauses.includes(clause) ? 'selected' : ''}>
                <input type="checkbox" checked={selectedClauses.includes(clause)} onChange={() => toggleClause(clause)} />
                <span>{clause}</span>
              </label>
            ))}
          </div>
        </section>
        <section className="m10-action-card">
          <div className="m10-title-row">
            <span>Contingency Guidance</span>
            <strong>AI Review</strong>
          </div>
          <div className="m10-guidance-list">
            <div>
              <strong>Buyer Protection</strong>
              <p>Keep diligence, title, physical inspection, and financing protections aligned with underwriting risk.</p>
            </div>
            <div>
              <strong>Seller Confidence</strong>
              <p>Use shorter expiration and exclusivity windows when you need urgency without weakening diligence rights.</p>
            </div>
            <div>
              <strong>PSA Handoff</strong>
              <p>Selected contingencies flow directly into Module 11 for clause review and PSA drafting.</p>
            </div>
          </div>
        </section>

        <section className="m10-contingency-summary-card">
          <div className="m10-title-row">
            <span>Contingency Impact</span>
            <strong>Review</strong>
          </div>
          <div className="m10-contingency-metrics">
            <div>
              <span>Selected Clauses</span>
              <strong>{selectedClauses.length}/{allClauses.length}</strong>
              <p>Coverage carried into PSA drafting.</p>
            </div>
            <div>
              <span>Diligence Window</span>
              <strong>{terms.dueDiligenceDays} days</strong>
              <p>{Number(terms.dueDiligenceDays) >= 45 ? 'Buyer has adequate review time.' : 'Consider expanding review period.'}</p>
            </div>
            <div>
              <span>Financing Risk</span>
              <strong>{terms.financingContingency ? 'Protected' : 'Exposed'}</strong>
              <p>{terms.financingContingency ? 'Debt approval is preserved as a condition.' : 'Buyer carries more closing risk.'}</p>
            </div>
            <div>
              <span>PSA Handoff</span>
              <strong>{selectedClauses.length ? 'Ready' : 'Incomplete'}</strong>
              <p>These clauses become the starting checklist for Module 11.</p>
            </div>
          </div>
        </section>
        </>
        )}
      </div>

      <div className="module-bottom-advance">
        <p>Finalize the LOI only after offer terms, contingencies, and preview text are reviewed.</p>
        <button className="m10-bottom-advance-btn" onClick={handleComplete}>
          Finalize LOI &amp; Advance to Module 11
        </button>
      </div>
    </div>
  )
}

export default Module10Page
